import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  notificationMethodOptions,
  referralTypeCard,
  notificationTypeOptions,
  pharmacyLocationOptions,
  ARBillingOptions,
} from "./data";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import {
  updateClinicalService,
  updateMinorAilmentStatus,
} from "../../../../../redux/modules/clinical-services/actions";
import ReferralCreate from "./ReferralCreate";
import SubNavigation from "../../common/sub-navigation";
import {
  calculateAgeYear,
  formatDateToDD_MMM_YYYY,
  formatDateToYYYYMMDD,
  getTimeDifference,
} from "../../../../../helpers/functions";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import NotificationCreate from "./NotificationCreate";
import NotificationCard from "./notification-info-card";
import ReferralCard from "./referral-info-card";
import { getValidStep } from "../../../text/data";
import { GetReferralDetails } from "../documentation/data";
import { getCurrentUserDetails } from "../../../../../redux/modules/user/actions";
import {
  buildNotificationDocumentationFields,
  buildPrimaryProviderPayload,
  getMinorAilmentCombinedCardDerivedState,
} from "./referrals-shared";

const Referrals = () => {
  const {
    clinicalServiceObj,
    clinicalServiceName,
    clinicalServiceSteps,
  } = useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);
  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { id: patientId, serviceId } = useParams();
  const navigate = useNavigate();
  const {
    currentStep,
    currentSubStep,
    showReferral,
    startDateTime,
    endDateTime,
    documentation,
  } = clinicalServiceObj.initialAssessment.initialAssessmentForm;
  let showReferral2 = true;
  //referral constants
  const [referralType, setReferralType] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [createRefferal, setCreateRefferal] = useState(true);
  const [showReferralSlider, setShowReferralSlider] = useState(false);
  const [newReferralType, setNewReferralType] = useState(null);

  //notification constants
  const [notificationType, setNotificationType] = useState(null);
  const [dateOfNotification, setDateOfNotification] = useState(null);
  const [methodOfNotification, setMethodOfNotification] = useState(null);
  const [notificationInfo, setNotificationInfo] = useState(null);
  const [createNotification, setCreateNotification] = useState(true);
  const [showNotificationSlider, setShowNotificationSlider] = useState(false);
  const [newNotificationType, setNewNotificationType] = useState(null);

  const [subStepComplete, setSubStepComplete] = useState(false);
  const [openServiceCompleteDialog, setOpenServiceCompleteDialog] =
    useState(false);
  const [
    openAgeAlertwithNoReferralDialog,
    setOpenAgeAlertwithNoReferralDialog,
  ] = useState(false);
  const [openreferralConfirmationDialog, setOpenreferralConfirmationDialog] =
    useState(false);
  const [openreferralDeleteDialog, setOpenreferralDeleteDialog] =
    useState(false);
  const [
    openNotificationConfirmationDialog,
    setOpenNotificationConfirmationDialog,
  ] = useState(false);
  const [openNotificationDeleteDialog, setOpenNotificationDeleteDialog] =
    useState(false);
  const [billingInformation, setBillingInformation] = useState();
  const [pharmacyLocation, setpharmacyLocation] = useState(null);
  const [treatmentDrugs, setTreatmentDrugs] = useState([]);
  const [serviceEndTime, setServiceEndTime] = useState(
    new Date().toISOString()
  );
  const [notificationOptions, setNotificationOptions] = useState(
    notificationTypeOptions
  );
  const [alertData, setAlertData] = useState(null);
  const referralDecisionKey = `referralDecisions_${serviceId}`;
  const referralDecisions =
    typeof sessionStorage !== "undefined"
      ? JSON.parse(sessionStorage.getItem(referralDecisionKey) || "[]")
      : [];
  const shouldForceNoReferral =
    Array.isArray(referralDecisions) &&
    referralDecisions.length > 0 &&
    referralDecisions.every((decision) => decision === false);
  const shouldShowReferral = shouldForceNoReferral
    ? false
    : alertData
      ? alertData.ContinueWithReferral === true
      : showReferral;
  const selectedPrimaryProvider =
    patient?.careProvider?.find((provider) => provider?.primaryProvider) ||
    patient?.careProvider?.[0] ||
    null;

  // Helper to compute pharmacist full name once (same as documentation checklist)
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  // Fallback: hydrate user details if missing
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      if (userId) dispatch(getCurrentUserDetails(userId, false));
    }
  }, [currentLoggedInUserDetail, dispatch]);


  useEffect(() => {
    if (shouldShowReferral)
      setNotificationOptions(
        referralType === "No Referral"
          ? notificationTypeOptions.filter(
              (x) => x.label !== "Same as Referral Type"
            )
          : notificationTypeOptions
      );
    else
      setNotificationOptions(
        notificationTypeOptions.filter(
          (x) => x.label !== "Same as Referral Type"
        )
      );
  }, [shouldShowReferral, referralType]);

  const onNext = async () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );

      const updatedSteps = {
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        startDateTime: startDateTime ?? new Date().toISOString(),
        endDateTime:
          startDateTime === endDateTime ? serviceEndTime : endDateTime,
        serviceSteps: clinicalServiceSteps,
        showReferral: shouldShowReferral,
        documentation: {
          ...documentation,
          referralType: [referralType],
          notificationType: [notificationType],
          dateOfNotification: dateOfNotification,
          methodOfNotification: methodOfNotification,
          billingInformation: billingInformation,
          pharmacyLocation: pharmacyLocation,
        },
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onSubmit = () => {
    dispatch(updateMinorAilmentStatus(clinicalServiceObj.id, "refer"));
    navigate(`/patient/${clinicalServiceObj.patientId}/clinical-services/`);
  };

  const handleNextClick = () => {
    if (alertData?.EndServiceAndRefer) {
      if (
        (!referralType || referralType === "No Referral") &&
        notificationType === "nonApplicable"
      )
        setOpenAgeAlertwithNoReferralDialog(true);
      else setOpenAgeAlertwithNoReferralDialog(true);
    } else handleBillingDialog();
  };

  const handleBillingDialog = () => {
    setBillingInformation(treatmentDrugs.length > 0 ? "PIIP" : "NPIIP");
    setOpenServiceCompleteDialog(true);
  };
  const applyNotificationDefaults = (type, existingDate = null, existingMethod = null) => {
    if (type === "nonApplicable") {
      setDateOfNotification(null);
      setMethodOfNotification(null);
      return;
    }

    setDateOfNotification(
      existingDate ? formatDateToYYYYMMDD(existingDate) : formatDateToYYYYMMDD(new Date())
    );
    setMethodOfNotification(existingMethod || "fax");
  };

  const saveNotificationInformation = (
    info,
    newType,
    isUpdate = false,
    selectedTypeOverride = null,
    docFieldOverrides = null
  ) => {
    const docFields = buildNotificationDocumentationFields({
      info,
      newType,
      selectedTypeOverride,
      docFieldOverrides,
      notificationDate: dateOfNotification,
      methodOfNotification: methodOfNotification,
    });

    const updatedSteps = {
      currentStep: currentStep,
      currentSubStep: currentSubStep,
      showReferral: shouldShowReferral,
      documentation: {
        ...documentation,
        ...docFields,
      },
    };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  const useExistingProvider = (isReferral) => {
    const existingProvider = selectedPrimaryProvider;
    if (!existingProvider) return;
    const primaryProvider = buildPrimaryProviderPayload(existingProvider);
    if (
      isReferral &&
      !documentation?.referralInformations?.some(
        (x) => x.referralType === "Primary Provider"
      )
    ) {
      saveReferralInformation(primaryProvider, false);
    } else if (
      !documentation?.notificationInformations?.some(
        (x) => x.referralType === "Primary Provider"
      )
    ) {
      saveNotificationInformation(primaryProvider, "Primary Provider", false);
    }
  };

  const hasProviderDetails = (items) =>
    Array.isArray(items) && items.length > 0;

  const {
    referralInformation,
    notificationInformation,
    combinedCardInformation,
    showCombinedProviderInfoCard,
  } = useMemo(
    () =>
      getMinorAilmentCombinedCardDerivedState({
        documentation,
        shouldShowReferral,
        referralType,
        notificationType,
      }),
    [
      documentation?.referralInformations,
      documentation?.notificationInformations,
      shouldShowReferral,
      referralType,
      notificationType,
    ]
  );

  useEffect(() => {
    if (!shouldShowReferral) {
      if (referralType !== "No Referral") {
        setReferralType("No Referral");
      }

      const hasNotificationInfo =
        (documentation?.notificationInformations?.length ?? 0) > 0;
      // Default to Non-Applicable only when notification is still unset, as a local fallback to avoid overwriting a saved selection.
      if (notificationType === null && !hasNotificationInfo) {
        setNotificationType("nonApplicable");
      }
      return;
    }

    if (!selectedPrimaryProvider) return;

    const shouldAutoFillReferral =
      shouldShowReferral &&
      !hasProviderDetails(documentation?.referralInformations) &&
      !referralType;
    const shouldAutoFillNotification =
      shouldShowReferral &&
      !hasProviderDetails(documentation?.notificationInformations) &&
      !notificationType;

    if (shouldAutoFillReferral) {
      setReferralType("Primary Provider");
      useExistingProvider(true);
    }

    if (shouldAutoFillNotification) {
      setNotificationType("Primary Provider");
      useExistingProvider(false);
    }
  }, [
    selectedPrimaryProvider,
    documentation?.referralInformations,
    documentation?.notificationInformations,
    shouldShowReferral,
    referralType,
    notificationType,
  ]);

  const saveReferralInformation = (info, isUpdate = false) => {
    const obj = { ...info, comments: "referral" };
    let updatedReferalls = [];
    let updatedReferallInfo = [];

    if (info !== null) {
      updatedReferalls = [obj.referralType];
      updatedReferallInfo = [obj];
    } else {
      //if we have un seleceted any referral type or selected No Referral
      updatedReferalls = ["No Referral"];
      updatedReferallInfo = [];
    }

    const updatedSteps = {
      currentStep: currentStep,
      currentSubStep: currentSubStep,
      showReferral: shouldShowReferral,
      documentation: {
        ...documentation,
        referralType: updatedReferalls,
        referralInformations: updatedReferallInfo,
      },
    };
    dispatch(updateClinicalService(serviceId, updatedSteps, isUpdate));
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = {
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      showReferral: shouldShowReferral,
    };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  //useefect section
  useEffect(() => {
    setAlertData(
      GetReferralDetails(
        clinicalServiceObj?.initialAssessment?.initialAssessmentForm
      )
    );
  }, [clinicalServiceObj?.initialAssessment?.initialAssessmentForm]);

  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm.documentation
    ) {
      setSubStepComplete(true);
      setReferralType(
        documentation.referralType ? documentation.referralType[0] : null
      );
      setNotificationType(
        documentation.notificationType
          ? documentation.notificationType[0]
          : null
      );
      setBillingInformation(documentation.billingInformation);
      setpharmacyLocation(documentation.pharmacyLocation);
    }
  }, [
    clinicalServiceObj.initialAssessment.initialAssessmentForm.documentation,
  ]);

  useEffect(() => {
    if (clinicalServiceObj.initialAssessment.initialAssessmentForm.treatment) {
      setTreatmentDrugs([]);
      const treatment =
        clinicalServiceObj.initialAssessment.initialAssessmentForm.treatment;
      const prescriptions = [
        treatment?.firstLineTherapyPrescriptions,
        treatment?.stepUpTherapyPrescriptions,
        treatment?.alternativeFirstLineTherapyPrescriptions,
        treatment?.addOnTherapyOralDecongestantsPrescriptions,
        treatment?.addOnTherapyIntranasalDecongestantsPrescriptions,
        treatment?.addOnTherapyOralAntihistaminesPrescriptions,
        treatment?.nasalSalineRinsePrescriptions,
        treatment?.nasalSalineSprayPrescriptions,
        treatment?.nasalLubricantPrescriptions,
      ];

      let combinedDrugs = prescriptions
        .filter((list) => Array.isArray(list) && list.length > 0)
        .flat();

      if (combinedDrugs.length > 0) {
        // apply any per-drug overrides saved in sessionStorage so rollbacks reflect last-updated values
        const storedMap = JSON.parse(sessionStorage.getItem('treatmentPharmacyLocations') || "{}");

        combinedDrugs = combinedDrugs.map((x) => ({
          ...x,
          pharmacyLocation: (x?.id && storedMap[x.id]) ?? x?.pharmacyLocation ?? "current",
        }));
        setTreatmentDrugs(combinedDrugs);
      }
    }
  }, [clinicalServiceObj.initialAssessment.initialAssessmentForm.treatment]);

  useEffect(() => {
    if (notificationType === "nonApplicable") {
      setDateOfNotification(null);
      setMethodOfNotification(null);
    } else {
      setDateOfNotification(
        documentation?.dateOfNotification
          ? formatDateToYYYYMMDD(documentation?.dateOfNotification)
          : formatDateToYYYYMMDD(new Date())
      );
      setMethodOfNotification(
        documentation?.methodOfNotification
          ? documentation?.methodOfNotification
          : "fax"
      );
    }
  }, [notificationType]);

  useEffect(() => {
    setSubStepComplete(checkValidation());
  }, [
    notificationType,
    referralType,
    dateOfNotification,
    methodOfNotification,
    documentation?.referralInformations,
    documentation?.notificationInformations,
  ]);

  const checkValidation = () => {
    if ((shouldShowReferral && referralType === null) || notificationType === null)
      return false;

    if (
      notificationType !== null &&
      notificationType !== "nonApplicable" &&
      (dateOfNotification === null || methodOfNotification === null)
    )
      return false;

    return true;
  };

  const handleReferralSelection = (value) => {
    //if directly select no referral
    if (value === "No Referral") {
      setNewReferralType(value);
      setOpenreferralConfirmationDialog(true);
    }

    //if ref type is changed
    if (referralType !== value) {
      //we have existing ref -> show confirmation to change ref.
      if (documentation?.referralInformations?.length > 0) {
        setNewReferralType(value);
        setOpenreferralConfirmationDialog(true);
      } else {
        //no existing ref then Add new ref
        setReferralType(value);
        setCreateRefferal(true);
        if (
          value === "Primary Provider" &&
          patient.careProvider?.some((x) => x.primaryProvider)
        )
          useExistingProvider(true);
        else if (value !== "No Referral") setShowReferralSlider(true);
      }
    }
  };

  const handleNotificationSelection = (value) => {
    //if not type is changed
    if (notificationType !== value) {
      //we have existing not -> show confirmation to change not.
      if (documentation?.notificationInformations?.length > 0) {
        setNewNotificationType(value);
        setOpenNotificationConfirmationDialog(true);
      } else {
        //no existing ref then Add new ref
        setNotificationType(value);
        setCreateNotification(true);
        applyNotificationDefaults(
          value,
          documentation?.dateOfNotification,
          documentation?.methodOfNotification
        );
        if (
          value === "Primary Provider" &&
          patient.careProvider?.some((x) => x.primaryProvider)
        )
          useExistingProvider(false);
        else if (value === "same") {
          const referralInfo = documentation?.referralInformations?.[0] || null;
          if (referralInfo) {
            saveNotificationInformation(referralInfo, value, false, "same");
          }
        } else if (
          shouldShowReferral &&
          referralType === value &&
          documentation?.referralInformations?.[0]
        ) {
          const todayYmd = formatDateToYYYYMMDD(new Date());
          const resolvedMethod =
            documentation?.methodOfNotification ||
            methodOfNotification ||
            "fax";
          applyNotificationDefaults(value, todayYmd, resolvedMethod);
          saveNotificationInformation(
            {
              ...documentation.referralInformations[0],
              referralType: value,
            },
            value,
            false,
            undefined,
            {
              dateOfNotification: todayYmd,
              methodOfNotification: resolvedMethod,
            }
          );
        } else if (value !== "nonApplicable" && value !== "same")
          setShowNotificationSlider(true);
      }
    }
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-2.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  {shouldShowReferral ? "Referrals & Notifications" : "Notifications"}
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div className="flex flex-row gap-6 overflow-auto">
                {/* referral card */}
                {shouldShowReferral && (
                  <>
                    <div className="h-[595px] min-w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      <div
                        className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                        style={{
                          backgroundImage: `url(/icons/texture-background.png)`,
                        }}
                      >
                        <h3 className="text-xl font-bold text-teal-600 ">
                          Referral Type
                        </h3>
                      </div>
                      <div className="flex flex-col gap-5 pl-5 py-5 pr-2 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                        <div className="flex flex-col gap-4">
                          <p className="text-lg font-semibold text-gray-800">
                            {referralTypeCard.question}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {referralTypeCard.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  handleReferralSelection(option.value);
                                }}
                                className={`font-regular text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                            hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600 w-full ${
                              referralType === option.value
                                ? "bg-gray-100 border-gray-600"
                                : "bg-white border-gray-100"
                            }`}
                              >
                                <p className="font-regular text-body-md flex justify-start items-start">
                                  {option.label}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {/* referral informations */}
                    {!showCombinedProviderInfoCard &&
                      documentation?.referralInformations &&
                      documentation?.referralInformations?.length > 0 &&
                      documentation?.referralInformations.map((ref) => (
                        <ReferralCard
                          clinicalServiceName={clinicalServiceName}
                          referral={ref}
                          onEdit={() => {
                            setReferralInfo(ref);
                            setCreateRefferal(false);
                            setShowReferralSlider(true);
                          }}
                          onDelete={() => {
                            setOpenreferralDeleteDialog(true);
                          }}
                        />
                      ))}
                  </>
                )}

                {/* notification card */}
                <div
                  id="notificationOfService"
                  className={`h-[595px] min-w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col`}
                >
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Notification of Service
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 pl-5 py-5 pr-2 overflow-scroll overflow-x-hidden bg-scroll min-h-[595px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Notification Type
                      </p>
                    </div>
                    <div>
                      <CustomInput
                        options={notificationOptions}
                        name="notificationType"
                        type="dropdown"
                        value={notificationType}
                        onChange={(e) => {
                          handleNotificationSelection(e.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Date of Notification
                      </p>
                    </div>
                    <div>
                      <CustomInput
                        name="dateOfNotification"
                        type="date"
                        disabled={notificationType === "nonApplicable"}
                        value={dateOfNotification}
                        onChange={(value) => setDateOfNotification(value)}
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Method of Notification
                      </p>
                    </div>
                    <div>
                      <CustomInput
                        options={notificationMethodOptions}
                        name="methodOfNotification"
                        type="dropdown"
                        disabled={notificationType === "nonApplicable"}
                        value={methodOfNotification}
                        onChange={(e) => {
                          setMethodOfNotification(e.value);
                        }}
                        shortMenuHeight={true}
                      />
                    </div>
                  </div>
                </div>

                {/* notification informations */}
                {showCombinedProviderInfoCard && combinedCardInformation && (
                  <NotificationCard
                    notification={combinedCardInformation}
                    title="Referral & Notification Information"
                    typeLabel="Referral / Notification Type"
                    onEdit={() => {
                      if (notificationType === "same") {
                        setReferralInfo(referralInformation);
                        setCreateRefferal(false);
                        setShowReferralSlider(true);
                      } else {
                        setNotificationInfo(combinedCardInformation);
                        setCreateNotification(false);
                        setShowNotificationSlider(true);
                      }
                    }}
                    onDelete={() => {
                      if (notificationType === "same") {
                        setOpenreferralDeleteDialog(true);
                      } else {
                        setOpenNotificationDeleteDialog(true);
                      }
                    }}
                  />
                )}
                {!showCombinedProviderInfoCard &&
                  documentation?.notificationInformations &&
                  documentation?.notificationInformations?.length > 0 &&
                  documentation?.notificationInformations.map((not) => (
                    <NotificationCard
                      notification={not}
                      onEdit={() => {
                        setNotificationInfo(not);
                        setCreateNotification(false);
                        setShowNotificationSlider(true);
                      }}
                      onDelete={() => {
                        setOpenNotificationDeleteDialog(true);
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className=" w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`disabled:opacity-40 w-[200px] max-h-[52px] !rounded-[30px] `}
            disabled={!subStepComplete}
            onClick={() => {
              handleNextClick();
            }}
          />
        </div>
      </div>

      {/* create referral form */}
      <ReferralCreate
        create={createRefferal}
        type={referralType}
        updateType={(val) => {
          setReferralType(val);
        }}
        open={showReferralSlider}
        existingReferral={referralInfo}
        onClose={(value) => {
          setCreateRefferal(true);
          setShowReferralSlider(false);
        }}
        onSave={(info) => {
          if (shouldShowReferral) {
            saveReferralInformation(info, false);
            if (notificationType === "same") {
              saveNotificationInformation(info, "same", false, "same");
            }
          }
          setCreateRefferal(true);
          setShowReferralSlider(false);
        }}
        onUpdate={(info) => {
          if (shouldShowReferral) {
            saveReferralInformation(info, true);
            if (notificationType === "same") {
              saveNotificationInformation(info, "same", true, "same");
            }
          }
          setCreateRefferal(true);
          setShowReferralSlider(false);
        }}
        referralOptions={referralTypeCard.options}
      />

      {/* create notification form */}
      <NotificationCreate
        create={createNotification}
        type={notificationType}
        updateType={(val) => {
          setNotificationType(val);
        }}
        open={showNotificationSlider}
        existingNotification={notificationInfo}
        onClose={(value) => {
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
        onSave={(info) => {
          saveNotificationInformation(info, newNotificationType, false);
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
        onUpdate={(info) => {
          saveNotificationInformation(info, newNotificationType, true);
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
      />

      {/* assessment complete dialog */}
      <ConfirmationModalNew
        open={openServiceCompleteDialog}
        title="Confirm Allergic Rhinitis Assessment Complete"
        className="!w-[720px] !max-w-[720px] [&_h3]:text-teal-300"
        confirmText="Complete"
        disableConfirm={billingInformation === "" || pharmacyLocation === ""}
        content={() => (
          <div>
            <div className="flex justify-between">
              <div>
                <span className="text-gray-600">
                  <b>Date:</b> {formatDateToDD_MMM_YYYY(new Date())}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  <b>Time Spent:</b>{" "}
                  {getTimeDifference(
                    startDateTime,
                    startDateTime === endDateTime ? serviceEndTime : endDateTime
                  )}
                </span>
              </div>
            </div>
            <div className="flex justify-start mt-2">
              <div>
                <span className="text-gray-600">
                  <b>Pharmacist:</b> {pharmacistName}
                </span>
              </div>
            </div>
            <div className="py-6 text-base text-gray-800">
              <p className="text-lg font-semibold pb-2">
                May Fill Prescription at Pharmacy of Choice
              </p>
              <ul className="">
                {treatmentDrugs.map((x) => (
                  <li className=" flex flex-row py-2">
                    <div className="p-3 border-[1px] flex-1 min-w-0 border-gray-300">
                      <p className="text-xs pb-[6px]">Prescription</p>
                      <p className="truncate" title={x.medicationName}>
                        {x.medicationName}
                      </p>
                    </div>
                    <div className="w-64 flex-shrink-0">
                      <CustomInput
                        options={pharmacyLocationOptions}
                        name="pharmacyLocation"
                        className="ml-4 !min-h-20 !max-h-36"
                        label="Pharmacy Location"
                        type="dropdown"
                        showInputCancellation={true}
                        value={x.pharmacyLocation}
                        required
                        onChange={(e) => {
                          const newDrugs = treatmentDrugs.map((d) =>
                            d.id === x.id ? { ...d, pharmacyLocation: e.value } : d
                          );
                          setTreatmentDrugs(newDrugs);
                          try {
                            sessionStorage.setItem(
                              'treatmentPharmacyLocations',
                              JSON.stringify(Object.fromEntries(newDrugs.map((d) => [d.id, d.pharmacyLocation])))
                            );
                          } catch {}
                          window.dispatchEvent(new Event('treatmentPharmacyLocationsChanged'));
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <CustomInput
              options={ARBillingOptions}
              name="billingInformation"
              className="col-span-12"
              label="Billing Information"
              type="dropdown"
              value={billingInformation}
              required
              onChange={(e) => setBillingInformation(e.value)}
              shortMenuHeight={true}
            />
            <div className="flex justify-between pt-2">
              <div>
                <span className="text-teal-300 text-sm">
                It is the Pharmacist's responsibility to ensure the PIN is accurate. {"   "}({" "}
                  <a
                    className="underline text-teal-300 text-sm font-semibold"
                    target="_blank"
                    href="https://www.ontario.ca/files/2024-01/moh-executive-officer-notice-en-2024-01-04.pdf"
                  >
                    Reference
                  </a>{" "}
                  )
                </span>
              </div>
            </div>
          </div>
        )}
        onCross={() => setOpenServiceCompleteDialog(false)}
        handleClose={() => setOpenServiceCompleteDialog(false)}
        confirmAction={() => {
          onNext();
        }}
      />
      <ConfirmationModalNew
        open={openAgeAlertwithNoReferralDialog}
        title="Assessment Cancelled"
        className="!min-w-[600px]"
        confirmText={"Continue to Documentation"}
        cancelText={"Cancel"}
        content={() => (
          <div className="">
            <p>Minor Ailment assessment has been cancelled.</p>
            {referralType === "No Referral" &&
              notificationType === "nonApplicable" && (
                <p>No referral or notification type has been selected.</p>
              )}
            <br />
            <p>
              Available documentation includes the Patient Referral
              {calculateAgeYear(patient?.dateOfBirth) > 2 &&
                ", Primary Provider Referral, Communication for Billing"}{" "}
              and Pharmacist Assessment Forms.
            </p>
          </div>
        )}
        onCross={() => setOpenAgeAlertwithNoReferralDialog(false)}
        handleClose={() => setOpenAgeAlertwithNoReferralDialog(false)}
        confirmAction={() => {
          setOpenAgeAlertwithNoReferralDialog(false);
          setServiceEndTime(new Date().toISOString());
          if (calculateAgeYear(patient?.dateOfBirth) > 2) {
            handleBillingDialog();
          } else onNext();
        }}
      />

      {/* confirm referral update dialog */}
      <ConfirmationModalNew
        open={openreferralConfirmationDialog}
        title="Confirm Referral Update"
        className="!min-w-[555px]"
        confirmText={
          newReferralType === "No Referral"
            ? "Confirm & Continue"
            : "Update Referral"
        }
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              {newReferralType === "No Referral"
                ? "Pharmacist Clinical Decision to Continue with NO Referral"
                : "Do you wish to change the Referral Type?"}
            </div>
          </div>
        )}
        onCross={() => setOpenreferralConfirmationDialog(false)}
        handleClose={() => setOpenreferralConfirmationDialog(false)}
        confirmAction={() => {
          setReferralType(newReferralType);
          if (newReferralType === "No Referral") {
            saveReferralInformation(null);
          } else if (
            newReferralType === "Primary Provider" &&
            patient?.careProvider?.some((x) => x.primaryProvider)
          ) {
            useExistingProvider(true);
          } else {
            setCreateRefferal(true);
            setShowReferralSlider(true);
          }
          setOpenreferralConfirmationDialog(false);
        }}
      />
      {/* delete referral dialog */}
      <ConfirmationModalNew
        open={openreferralDeleteDialog}
        title="Delete Referral Type"
        className="!min-w-[555px]"
        confirmText={"Delete"}
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              {"Do you wish to delete the Referral Type?"}
            </div>
          </div>
        )}
        onCross={() => setOpenreferralDeleteDialog(false)}
        handleClose={() => setOpenreferralDeleteDialog(false)}
        confirmAction={() => {
          setReferralType("No Referral");
          saveReferralInformation(null);
          setOpenreferralDeleteDialog(false);
        }}
      />

      {/* confirm notification update dialog */}
      <ConfirmationModalNew
        open={openNotificationConfirmationDialog}
        title="Confirm Notification Update"
        className="!min-w-[555px]"
        confirmText="Update Notification"
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              Do you wish to change the Notification Type?
            </div>
          </div>
        )}
        onCross={() => setOpenNotificationConfirmationDialog(false)}
        handleClose={() => setOpenNotificationConfirmationDialog(false)}
        confirmAction={() => {
          setNotificationType(newNotificationType);
          applyNotificationDefaults(
            newNotificationType,
            documentation?.dateOfNotification,
            documentation?.methodOfNotification
          );
          if (
            newNotificationType === "nonApplicable" ||
            newNotificationType === "same"
          ) {
            if (newNotificationType === "same") {
              const referralInfo = documentation?.referralInformations?.[0] || null;
              saveNotificationInformation(
                referralInfo,
                newNotificationType,
                false,
                "same"
              );
            } else {
              saveNotificationInformation(null, newNotificationType);
            }
          } else if (
            newNotificationType === "Primary Provider" &&
            patient.careProvider?.some((x) => x.primaryProvider)
          ) {
            useExistingProvider(false);
          } else if (
            shouldShowReferral &&
            referralType === newNotificationType &&
            documentation?.referralInformations?.[0]
          ) {
            const todayYmd = formatDateToYYYYMMDD(new Date());
            const resolvedMethod =
              documentation?.methodOfNotification ||
              methodOfNotification ||
              "fax";
            saveNotificationInformation(
              {
                ...documentation.referralInformations[0],
                referralType: newNotificationType,
              },
              newNotificationType,
              false,
              undefined,
              {
                dateOfNotification: todayYmd,
                methodOfNotification: resolvedMethod,
              }
            );
          } else {
            setCreateNotification(true);
            setShowNotificationSlider(true);
          }
          setOpenNotificationConfirmationDialog(false);
        }}
      />
      {/* delete notification dialog */}
      <ConfirmationModalNew
        open={openNotificationDeleteDialog}
        title="Delete Notification Type"
        className="!min-w-[555px]"
        confirmText="Delete"
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              Do you wish to delete the Notification Type?
            </div>
          </div>
        )}
        onCross={() => setOpenNotificationDeleteDialog(false)}
        handleClose={() => setOpenNotificationDeleteDialog(false)}
        confirmAction={() => {
          setNotificationType("nonApplicable");
          saveNotificationInformation(null, "nonApplicable");
          setOpenNotificationDeleteDialog(false);
        }}
      />
    </div>
  );
};

export default Referrals;
